import { z } from 'zod';
import { userRoleSchema } from './enums.js';
/**
 * Contrats d'ADMINISTRATION (T7b, XPHOTO-25) — source unique front + back.
 *
 * Gestion des comptes (superadmin) et des accès lecture seule par sous-arbre
 * (`access_grants`). Aucun secret ne transite ici : le hash du mot de passe n'est
 * JAMAIS exposé, seul le mot de passe en clair de CRÉATION est accepté en entrée.
 */
// ---------------------------------------------------------------------------
// Utilisateurs
// ---------------------------------------------------------------------------
/** Item de liste (`GET /api/admin/users`). Jamais de hash de mot de passe. */
export const adminUserSchema = z.object({
    id: z.uuid(),
    email: z.string(),
    displayName: z.string(),
    role: userRoleSchema,
    /** Bibliothèque possédée (une par utilisateur au plus) ou `null`. */
    libraryId: z.uuid().nullable(),
    createdAt: z.string().datetime({ offset: true }),
});
export const adminUserListSchema = z.object({
    users: z.array(adminUserSchema),
});
/**
 * Corps de `POST /api/admin/users`. Le mot de passe (min. 8) est haché argon2id
 * côté serveur — jamais stocké/renvoyé en clair.
 *
 * `grantFullRead` (défaut serveur : `true`) accorde d'emblée au nouveau compte un
 * accès LECTURE SEULE à la racine de la (des) bibliothèque(s) — évite d'oublier le
 * grant sur une petite bibliothèque partagée. Sans effet pour un superadmin (accès
 * total) ni pour un admin (écriture sur toute la bibliothèque).
 */
export const createUserRequestSchema = z.object({
    email: z.string().email().max(320),
    displayName: z.string().min(1).max(200),
    role: userRoleSchema,
    password: z.string().min(8).max(1024),
    grantFullRead: z.boolean().optional(),
});
/**
 * Corps de `PATCH /api/admin/users/:id`. Au moins un champ requis (`refine`).
 * Modifie l'affichage, le rôle et/ou RÉINITIALISE le mot de passe (superadmin) :
 * `password` (optionnel, min. 8) est haché argon2id côté serveur — jamais stocké
 * ni renvoyé en clair, exactement comme `createUserRequestSchema`. Fournir
 * `password` révoque les sessions du compte cible (reconnexion forcée).
 */
export const updateUserRequestSchema = z
    .object({
    displayName: z.string().min(1).max(200).optional(),
    role: userRoleSchema.optional(),
    password: z.string().min(8).max(1024).optional(),
})
    .refine((v) => v.displayName !== undefined || v.role !== undefined || v.password !== undefined, {
    message: 'Au moins un champ à mettre à jour',
});
// ---------------------------------------------------------------------------
// Accès (grants lecture seule par sous-arbre)
// ---------------------------------------------------------------------------
/** Item de liste (`GET /api/libraries/:id/grants`). */
export const grantSchema = z.object({
    id: z.uuid(),
    userId: z.uuid(),
    libraryId: z.uuid(),
    /** Préfixe de `rel_path` accordé ; `''` = racine (toute la bibliothèque). */
    subtreePath: z.string(),
    /** Affichage : bénéficiaire (jointure `users`). */
    email: z.string(),
    displayName: z.string(),
    createdAt: z.string().datetime({ offset: true }),
});
export const grantListSchema = z.object({
    grants: z.array(grantSchema),
});
/**
 * Corps de `POST /api/libraries/:id/grants`. `subtreePath` par défaut `''`
 * (racine). La bibliothèque cible vient du chemin ; l'`userId` désigne le
 * bénéficiaire de l'accès lecture seule.
 */
export const createGrantRequestSchema = z.object({
    userId: z.uuid(),
    subtreePath: z.string().max(1024).default(''),
});
// ---------------------------------------------------------------------------
// Journaux de requêtes (superadmin) — journalisation de TOUTES les requêtes API
// ---------------------------------------------------------------------------
/** Borne haute de la fenêtre de listing (protège la DB + le rendu). */
export const REQUEST_LOG_MAX_LIMIT = 200;
export const REQUEST_LOG_DEFAULT_LIMIT = 50;
/**
 * Item de journal (`GET /api/admin/request-logs`). Une ligne = une requête `/api/*`
 * capturée par le hook `onResponse` (health exclu). `userEmail` est en clair
 * (exigence métier). Aucun secret / corps / query-string n'y figure.
 */
export const requestLogSchema = z.object({
    id: z.uuid(),
    ts: z.string().datetime({ offset: true }),
    method: z.string(),
    /** Chemin SANS query-string. */
    path: z.string(),
    /** Gabarit Fastify (`/api/assets/:id`) si disponible, sinon `null`. */
    route: z.string().nullable(),
    status: z.number().int(),
    durationMs: z.number().int().nonnegative(),
    userId: z.uuid().nullable(),
    /** E-mail de l'utilisateur résolu, en clair, ou `null` (non authentifié). */
    userEmail: z.string().nullable(),
    /** Message d'erreur tronqué (surtout 5xx), ou `null`. */
    error: z.string().nullable(),
    /** Corrélation `x-request-id` avec les logs serveur, ou `null`. */
    requestId: z.string().nullable(),
});
/** Page de journaux (`{ logs, total }`) — `total` dimensionne la pagination. */
export const requestLogListSchema = z.object({
    logs: z.array(requestLogSchema),
    total: z.number().int().nonnegative(),
});
/**
 * Méthode HTTP filtrable (barre de filtres « Méthode » des journaux de requêtes).
 * Restreinte aux verbes réellement journalisés par l'API.
 */
export const requestLogMethodSchema = z.enum(['GET', 'POST', 'PATCH', 'DELETE']);
/**
 * CLASSE de statut HTTP filtrable (barre de filtres « Statut »). On filtre par
 * classe (`2xx`/`3xx`/`4xx`/`5xx`) plutôt que par code exact : c'est le besoin réel
 * (« montre-moi les erreurs ») et `request_logs.status` est un entier → chaque
 * classe = un intervalle `[n*100, n*100+100)` (WHERE paramétré côté repo).
 */
export const requestLogStatusClassSchema = z.enum(['2xx', '3xx', '4xx', '5xx']);
/**
 * Query params de `GET /api/admin/request-logs`. `limit` borné (1..200, défaut 50),
 * `offset` ≥ 0 (défaut 0). Coercition depuis la querystring (string) via
 * `z.coerce.number()` — AJV de Fastify coerce, Zod re-valide (voir `assetQuerySchema`).
 *
 * Trois filtres optionnels (barre de filtres UI Lot C, cohérente avec les erreurs
 * de fichiers mais SÉMANTIQUE PROPRE aux requêtes) : `userId` (auteur de la
 * requête), `method` (verbe HTTP), `statusClass` (classe de statut HTTP).
 */
export const requestLogQuerySchema = z.object({
    limit: z.coerce
        .number()
        .int()
        .positive()
        .max(REQUEST_LOG_MAX_LIMIT)
        .default(REQUEST_LOG_DEFAULT_LIMIT),
    offset: z.coerce.number().int().nonnegative().default(0),
    userId: z.uuid().optional(),
    method: requestLogMethodSchema.optional(),
    statusClass: requestLogStatusClassSchema.optional(),
});
/**
 * Corps de `DELETE /api/admin/request-logs`. `before` (ISO 8601) optionnel :
 * absent → purge TOUT le journal ; présent → purge les entrées `ts < before`.
 */
export const purgeRequestLogsRequestSchema = z.object({
    before: z.string().datetime({ offset: true }).optional(),
});
/** Résultat de purge : nombre de lignes supprimées. */
export const purgeRequestLogsResultSchema = z.object({
    deleted: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Réinitialisation d'une bibliothèque (superadmin) — « repartir de zéro »
// ---------------------------------------------------------------------------
/**
 * Résultat de `POST /api/libraries/:id/reset`. Compteurs de ce qui a été
 * supprimé : lignes d'index médias (`assets` + enfants) et fichiers de vignettes.
 *
 * ⚠ SÛRETÉ PAR CONSTRUCTION : le reset ne touche QUE la base (index dérivé) et le
 * cache de vignettes (régénérable). Il ne lit ni n'écrit JAMAIS les fichiers
 * ORIGINAUX de la bibliothèque montée (`/library`, lecture seule) et n'exécute
 * jamais ExifTool — aucune métadonnée EXIF sur disque n'est modifiée. Les comptes,
 * la bibliothèque, les accès et le vocabulaire de tags sont préservés.
 */
export const resetLibraryResultSchema = z.object({
    /** Lignes `assets` supprimées (= médias désindexés). */
    deletedAssets: z.number().int().nonnegative(),
    /** Fichiers de vignettes supprimés du cache (best-effort). */
    deletedThumbnails: z.number().int().nonnegative(),
    /** Lignes `jobs` supprimées (historique des traitements de la bibliothèque). */
    deletedJobs: z.number().int().nonnegative(),
});
//# sourceMappingURL=admin.js.map